import React, { useEffect, useCallback, useRef } from 'react';
import { useIdleTimeoutContext } from 'containers/InactivityTimeout/IdleTimeoutContext';
import { post } from 'utils/api';
import PropTypes from 'prop-types';
import _omitBy from 'lodash/omitBy';
import _isNil from 'lodash/isNil';
import { makeStyles } from '@material-ui/core/styles';
import DialogContent from '@material-ui/core/DialogContent';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CodeInput from 'components/CodeInput';
import LoadingOverlay from 'components/LoadingOverlay';
// eslint-disable-next-line import/no-cycle
import ThemedDialog from 'components/ThemedDialog';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import MixpanelActions from 'utils/mixpanelService';
import {
  makeSelectAuth,
  makeSelectVerificationInfo,
  makeSelectVerificationLoading,
  makeSelectVerificationModal,
} from 'containers/Auth/selectors';
import {
  hideAuthCTAModal,
  hideVerificationModal,
  setAuthModalState,
  setVerificationInfo,
  toggleVerificationLoading,
  logout,
  handleStoredItem,
} from 'containers/Auth/actions';
import Config from 'utils/getEnvConfig';
import useContentfulLocale from 'components/useContentfulLocale';
import { useHistory } from 'react-router-dom';
import { getBrandValue } from 'utils/brandUtils';
import { CircularProgress } from '@material-ui/core';
import {
  useVerificationState,
  useLabels,
  useCountdownTimer,
  useLockoutTimer,
} from './hooks';
import {
  formatErrorMessage,
  calculateRemainingMinutes,
  buildVerificationPayload,
  buildVerifyCodePayload,
} from './utils';
import {
  RESEND_SUCCESS_DISPLAY_DURATION,
  ERROR_TYPES,
  VERIFICATION_TYPES,
  COMPONENT_TYPES,
} from './constants';
import VerificationSuccess from './VerificationSuccess';
import ResendCodeLink from './ResendCodeLink';

// Constants
const {
  LAMBDA: {
    FIREBASE_HELPER: {
      REQUEST_EMAIL_VERIFICATION_URL,
      VERIFY_EMAIL_URL,
      API_KEY,
    },
  },
} = Config;

const EMAIL_VERIFICATION_API_OPTIONS = {
  headers: {
    'x-api-key': API_KEY,
  },
};

// Styles
const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: '20px !important',
    [theme.breakpoints.up('800')]: {
      width: 650,
      maxWidth: 650,
    },
    [theme.breakpoints.down('799')]: {
      margin: '12px',
    },
  },
  contentRoot: {
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 65,
  },
  formContainer: {
    textAlign: 'center',
    maxWidth: '100%',
    padding: '0 5%',
    [theme.breakpoints.up('md')]: {
      maxWidth: 500,
      padding: 0,
    },
  },
  title: {
    fontSize: 18,
    marginTop: 1,
    marginBottom: 26,
    [theme.breakpoints.up('sm')]: {
      fontSize: 27,
    },
  },
  subtitle: {
    marginTop: 1,
    marginBottom: 18,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '22px',
    [theme.breakpoints.up('sm')]: {
      fontSize: 16,
    },
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    height: 48,
    marginTop: 24,
    marginBottom: 18,
    textTransform: 'uppercase',
    fontSize: 14,
    borderRadius: 8,
    '&:hover': {
      opacity: 0.8,
      backgroundColor: theme.palette.text.secondary,
    },
    ...theme.typography.pSmallBold,
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
      backgroundColor: theme.palette.text.secondary,
    },
  },
  disclaimer: {
    marginTop: 20,
    color: '#757575',
    ...theme.typography.pxSmall,
  },
  error: {
    color: '#E74C3C',
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '22px',
    marginTop: 14,
  },
  verificationSuccessContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 45px 45px',
    textAlign: 'center',
    marginBottom: 0,
    [theme.breakpoints.down('xs')]: {
      padding: '40px 24px 24px',
    },
  },
}));

const mapTrigger = trigger => {
  if (!trigger) return 'unknown';
  switch (trigger) {
    case VERIFICATION_TYPES.SIGN_UP:
      return 'sign up';
    case VERIFICATION_TYPES.EMAIL_CHANGE:
      return 'email update';
    default:
      return trigger.toLowerCase().replace(/_/g, ' ');
  }
};

const mapType = (type, isEmbedded) => {
  // If embedded, always return 'modal'
  if (isEmbedded) {
    return 'modal';
  }
  switch (type) {
    case COMPONENT_TYPES.MODAL:
      return 'modal';
    case COMPONENT_TYPES.PAGE:
      return 'full page';
    default:
      return type;
  }
};

const mapErrorToReason = errorType => {
  switch (errorType) {
    case ERROR_TYPES.ACCOUNT_LOCKED:
    case ERROR_TYPES.ACCOUNT_LOCKED_MAX_ATTEMPTS:
      return 'too_many_attempts_lockout';
    case ERROR_TYPES.RESEND_THROTTLE:
      return 'rate_limited_resend';
    case ERROR_TYPES.EMAIL_ALREADY_VERIFIED:
      return 'email_already_verified';
    case ERROR_TYPES.NEW_EMAIL_DIFFERENT_FROM_CURRENT_EMAIL:
      return 'mismatch_email';
    case ERROR_TYPES.INVALID_CODE:
      return 'invalid_code';
    default:
      return 'server_error';
  }
};

const useSelectors = () => {
  const verificationModalState = useSelector(makeSelectVerificationModal());
  const verificationInfo = useSelector(makeSelectVerificationInfo());
  const verificationLoading = useSelector(makeSelectVerificationLoading());
  const auth = useSelector(makeSelectAuth());
  const [siteCopy] = useSiteCopySelector(['verify-email']);

  return {
    verificationModalState,
    verificationInfo,
    verificationLoading,
    auth,
    siteCopy,
  };
};

// Main component
const VerificationCodeDialog = ({
  title,
  subtitle,
  resendLabel,
  submitLabel,
  successTitle,
  successButtonLabel,
  contentOnly,
  type,
  onSuccess,
  showLoadingOverlay,
  loading,
  isEmbedded,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const { idleTimer } = useIdleTimeoutContext();
  const ctfLocale = useContentfulLocale();
  const hasRequestedInitialVerification = useRef(false);
  const hasViewed = useRef(false);

  const {
    verificationModalState,
    verificationInfo,
    verificationLoading,
    auth,
    siteCopy,
  } = useSelectors();

  const {
    code,
    setCode,
    processing,
    setProcessing,
    error,
    setError,
    showSuccess,
    setShowSuccess,
    setVerificationResponse,
    countdownSeconds,
    setCountdownSeconds,
    isResendDisabled,
    setIsResendDisabled,
    resent,
    setResent,
    lockoutUntil,
    setLockoutUntil,
    setLockoutMinutes,
  } = useVerificationState();

  // Derived values
  const isSignup = verificationModalState?.info?.isSignUp || false;
  const showSuccessModal =
    verificationModalState?.info?.showSuccessModal || false;
  const isGuest = verificationModalState?.payload?.isGuest || false;

  const opened =
    type === COMPONENT_TYPES.PAGE ? true : verificationModalState.show;

  const trackEvent = useCallback(
    (eventName, properties = {}) => {
      const trigger =
        verificationModalState?.info?.type ?? VERIFICATION_TYPES.SIGN_UP;
      MixpanelActions.track(eventName, {
        ...properties,
        trigger: mapTrigger(trigger),
        type: mapType(type, isEmbedded),
      });
    },
    [verificationModalState, type],
  );

  const isEmailChange =
    verificationModalState?.info?.type === VERIFICATION_TYPES.EMAIL_CHANGE;

  const labels = useLabels(
    siteCopy,
    {
      title,
      subtitle,
      resendLabel,
      submitLabel,
      successTitle,
      successButtonLabel,
    },
    isSignup,
  );

  const history = useHistory();
  // Force logout and redirect to login for token-related errors
  const handleTokenExpiredLogout = useCallback(() => {
    dispatch(logout({ force: true, action: 'token-expired' }));
    history.replace('/');
  }, [dispatch, history]);
  // API data creation
  const createVerificationData = useCallback(
    () =>
      buildVerificationPayload({
        brand: getBrandValue(),
        locale: ctfLocale.locale,
        isGuest,
        email: verificationModalState?.payload?.email,
        type: verificationModalState?.info?.type,
        isEmailChange,
        newEmail: verificationModalState?.payload?.email,
        token: auth?.stsTokenManager?.accessToken,
        postVerificationAction: verificationInfo?.postVerificationAction,
      }),
    [
      ctfLocale.locale,
      isGuest,
      verificationModalState,
      auth,
      verificationInfo,
      isEmailChange,
    ],
  );

  // API calls
  const requestEmailVerification = useCallback(async () => {
    const data = createVerificationData();
    try {
      dispatch(toggleVerificationLoading(true));
      const response = await post(
        REQUEST_EMAIL_VERIFICATION_URL,
        _omitBy(data, _isNil),
        EMAIL_VERIFICATION_API_OPTIONS,
      );
      // Preserve postVerificationAction, type, and hasStoredItems from original data when storing API response
      // This ensures they're available for resend code requests and after verification
      const responseData = response?.data;
      const currentVerificationInfo = verificationInfo;
      dispatch(
        setVerificationInfo({
          ...responseData,
          ...(data.postVerificationAction && {
            postVerificationAction: data.postVerificationAction,
          }),
          ...(data.type && {
            type: data.type,
          }),
          ...(currentVerificationInfo?.hasStoredItems && {
            hasStoredItems: currentVerificationInfo.hasStoredItems,
          }),
        }),
      );
      // Clear error only on successful request
      setError('');
      return response;
    } catch (requestError) {
      dispatch(setVerificationInfo(requestError));
      return null;
    } finally {
      dispatch(toggleVerificationLoading(false));
    }
  }, [createVerificationData, dispatch, setError]);

  const handleResend = useCallback(async () => {
    trackEvent('Email Verification - Resend Clicked');
    try {
      setError('');
      await requestEmailVerification();
      setResent(true);
      setTimeout(() => setResent(false), RESEND_SUCCESS_DISPLAY_DURATION);
    } catch (resendError) {
      setError(resendError);
      setIsResendDisabled(false);
    }
  }, [
    requestEmailVerification,
    setError,
    setResent,
    setIsResendDisabled,
    trackEvent,
  ]);

  // Event handlers
  const handleCodeChange = useCallback(
    value => {
      setCode(value);
    },
    [setCode],
  );

  const resetLocalState = useCallback(() => {
    setShowSuccess(false);
    setVerificationResponse(null);
    setError('');
    setCountdownSeconds(0);
    setIsResendDisabled(false);
    setResent(false);
    setLockoutUntil(null);
    setLockoutMinutes(0);
    setCode('');
  }, [
    setShowSuccess,
    setVerificationResponse,
    setError,
    setCountdownSeconds,
    setIsResendDisabled,
    setResent,
    setLockoutUntil,
    setLockoutMinutes,
    setCode,
  ]);

  const handleClose = useCallback(
    event => {
      if (event) {
        trackEvent('Email Verification - Canceled', {
          cancel_action: 'close_button',
        });
      }
      // Reset local component state
      resetLocalState();

      // Ensure processing is reset when closing
      setProcessing(false);

      // Dispatch Redux actions
      dispatch(hideVerificationModal());
      dispatch(setVerificationInfo(null));
      dispatch(hideAuthCTAModal());
    },
    [resetLocalState, dispatch, setProcessing, trackEvent],
  );

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      setError('');
      setProcessing(true);

      try {
        const payload = buildVerifyCodePayload({
          email: verificationModalState?.email,
          code,
          isGuest,
          verificationId: verificationInfo?.verificationId,
          token: auth?.stsTokenManager?.accessToken,
        });

        const response = await post(
          VERIFY_EMAIL_URL,
          payload,
          EMAIL_VERIFICATION_API_OPTIONS,
        );

        if (response.status === 200) {
          trackEvent('Email Verification - Code Submitted', {
            response: 'valid_code',
          });
          trackEvent('Email Verification - Successful');
          // Handle customToken for EMAIL_CHANGE flow
          const verificationType = verificationModalState?.info?.type;
          if (
            verificationType === VERIFICATION_TYPES.EMAIL_CHANGE &&
            response.data?.customToken
          ) {
            await firebase
              .auth()
              .signInWithCustomToken(response.data.customToken);
          }
          // Always reload the current user to update emailVerified status
          if (firebase.auth().currentUser) {
            await firebase.auth().currentUser.reload();
          }

          setVerificationResponse(response);

          // Close the auth modal
          dispatch(setAuthModalState({ show: false, type: '' }));

          // Check if there are stored items (like course enrollment) to process after verification
          const hasStoredItems = verificationInfo?.hasStoredItems;

          // Clear postVerificationAction after successful verification
          dispatch(
            setVerificationInfo({
              ...verificationInfo,
              postVerificationAction: undefined,
            }),
          );

          if (verificationModalState?.info?.callback) {
            await verificationModalState.info.callback();
          }

          // Process stored items (like course enrollment) after verification
          if (hasStoredItems) {
            dispatch(handleStoredItem());
          }

          if (showSuccessModal) {
            setShowSuccess(true);
            // Reset processing after showing success modal
            setProcessing(false);
          } else if (onSuccess && typeof onSuccess === 'function') {
            await onSuccess();
            // Reset processing after onSuccess completes
            setProcessing(false);
          } else {
            // Reset processing and close modal
            setProcessing(false);
            resetLocalState();
            dispatch(hideVerificationModal());
            dispatch(setVerificationInfo(null));
            dispatch(hideAuthCTAModal());
          }
        } else {
          setError(labels.errorText);
          // Reset processing for non-200 responses
          setProcessing(false);
        }
      } catch (err) {
        // Handle API error responses
        const errorType = err?.type;
        let responseValue;
        if (errorType === ERROR_TYPES.INVALID_CODE) {
          responseValue = 'invalid_code';
        } else if (errorType === ERROR_TYPES.CODE_EXPIRED) {
          responseValue = 'expired_code';
        }

        if (responseValue) {
          trackEvent('Email Verification - Code Submitted', {
            response: responseValue,
          });
        }
        trackEvent('Email Verification - Failed', {
          reason: mapErrorToReason(errorType),
        });
        const lockedUntilTimestamp = err?.lockedUntil;

        // Store lockout info for countdown if account is locked
        if (
          (errorType === ERROR_TYPES.ACCOUNT_LOCKED ||
            errorType === ERROR_TYPES.ACCOUNT_LOCKED_MAX_ATTEMPTS) &&
          lockedUntilTimestamp
        ) {
          setLockoutUntil(lockedUntilTimestamp);
          const calculatedMinutes = calculateRemainingMinutes(
            lockedUntilTimestamp,
          );
          if (calculatedMinutes) {
            setLockoutMinutes(calculatedMinutes);
          }
        }

        // Format error message
        const displayError = formatErrorMessage(
          errorType,
          {
            remainingAttempts: err?.remainingAttempts,
            remainingMinutes:
              err?.remainingMinutes ||
              calculateRemainingMinutes(lockedUntilTimestamp),
            error: err?.error,
          },
          labels,
        );

        setError(displayError);

        // Force logout for token-related errors
        if (
          errorType === ERROR_TYPES.EXPIRED_TOKEN ||
          errorType === ERROR_TYPES.TOKEN_REQUIRED
        ) {
          handleTokenExpiredLogout();
        }

        // Reset processing for error cases
        setProcessing(false);
      }
    },
    [
      code,
      verificationModalState,
      isGuest,
      auth,
      verificationInfo,
      firebase,
      dispatch,
      showSuccessModal,
      labels,
      onSuccess,
      setError,
      setProcessing,
      setShowSuccess,
      setVerificationResponse,
      setLockoutUntil,
      setLockoutMinutes,
      handleTokenExpiredLogout,
      resetLocalState,
      trackEvent,
    ],
  );

  const handleSuccessOkay = useCallback(() => {
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    if (opened) {
      if (!hasViewed.current) {
        trackEvent('Email Verification - Viewed');
        hasViewed.current = true;
      }
      idleTimer.pause();

      // Reset local state when dialog opens (except for success state and processing)
      setError('');
      setResent(false);

      // Only call requestEmailVerification once when first opened
      // For SIGN_UP flow, wait for verificationInfo with postVerificationAction to be set by saga
      // For other flows (EMAIL_CHANGE, etc), call immediately even if verificationInfo is null
      const isSignUpFlow =
        verificationInfo?.type === VERIFICATION_TYPES.SIGN_UP;
      const isEmailChangeFlow =
        verificationInfo?.type === VERIFICATION_TYPES.EMAIL_CHANGE;
      const withSubscription = verificationInfo?.withSubscription || false;
      const hasPostVerificationAction =
        verificationInfo?.postVerificationAction;

      // Check if verificationInfo already has data (verificationId for guests, or valid type for authenticated)
      const hasExistingVerificationData =
        verificationInfo?.verificationId ||
        (isEmailChangeFlow && verificationInfo?.type);

      // Only wait for postVerificationAction if it's a SIGN_UP flow coming from modal state
      // but verificationInfo doesn't have the type set yet
      const shouldWaitForPostVerificationAction =
        isSignUpFlow && withSubscription && !hasPostVerificationAction;

      if (
        !hasRequestedInitialVerification.current &&
        !shouldWaitForPostVerificationAction
      ) {
        // Only call API if we don't already have verification data
        if (!hasExistingVerificationData) {
          requestEmailVerification();
        }
        hasRequestedInitialVerification.current = true;
      }
    } else {
      idleTimer.start();
      hasViewed.current = false;
      // Reset the flag when dialog closes so it can be called again when reopened
      hasRequestedInitialVerification.current = false;
    }
  }, [
    opened,
    idleTimer,
    requestEmailVerification,
    verificationInfo,
    setError,
    setResent,
    trackEvent,
  ]);
  // Use custom countdown timer hook
  useCountdownTimer(
    countdownSeconds,
    setCountdownSeconds,
    setIsResendDisabled,
    verificationInfo?.remainingSeconds,
  );

  // Handle verificationInfo errors
  useEffect(() => {
    // Only process if verificationInfo contains an error
    // A successful response will have verificationId, error responses won't
    const isSuccessResponse = verificationInfo?.verificationId;
    const hasError =
      !isSuccessResponse &&
      (verificationInfo?.error ||
        (verificationInfo?.type &&
          Object.values(ERROR_TYPES).includes(verificationInfo.type)));

    if (!hasError) return;

    const errorType = verificationInfo?.type;
    const lockedUntil = verificationInfo?.lockedUntil;

    // Handle lockout errors
    if (
      errorType === ERROR_TYPES.ACCOUNT_LOCKED ||
      errorType === ERROR_TYPES.ACCOUNT_LOCKED_MAX_ATTEMPTS
    ) {
      if (lockedUntil) {
        setLockoutUntil(lockedUntil);
      }

      const calculatedMinutes =
        verificationInfo?.remainingMinutes ||
        calculateRemainingMinutes(lockedUntil);

      if (calculatedMinutes) {
        setLockoutMinutes(calculatedMinutes);
      }
    }

    // Format and set error message
    const errorMessage = formatErrorMessage(
      errorType,
      {
        remainingSeconds: verificationInfo?.remainingSeconds,
        remainingMinutes:
          verificationInfo?.remainingMinutes ||
          calculateRemainingMinutes(lockedUntil),
        error: verificationInfo?.error,
      },
      labels,
    );

    setError(errorMessage);

    // Track specific error types
    if (
      [
        ERROR_TYPES.RESEND_THROTTLE,
        ERROR_TYPES.ACCOUNT_LOCKED,
        ERROR_TYPES.ACCOUNT_LOCKED_MAX_ATTEMPTS,
        ERROR_TYPES.NEW_EMAIL_IN_USE,
        ERROR_TYPES.EMAIL_ALREADY_VERIFIED,
      ].includes(errorType)
    ) {
      trackEvent('Email Verification - Failed', {
        reason: mapErrorToReason(errorType),
      });
    }
  }, [
    verificationInfo,
    labels,
    setError,
    setLockoutMinutes,
    setLockoutUntil,
    trackEvent,
  ]);

  // Use custom lockout timer hook
  useLockoutTimer(
    lockoutUntil,
    setLockoutUntil,
    setLockoutMinutes,
    setError,
    trackEvent,
    labels,
  );

  const renderSuccessContent = useCallback(
    () => (
      <VerificationSuccess
        title={successTitle || labels.successText}
        buttonLabel={labels.successButtonText}
        onOkay={handleSuccessOkay}
      />
    ),
    [
      successTitle,
      labels.successText,
      labels.successButtonText,
      handleSuccessOkay,
    ],
  );

  const renderVerificationForm = useCallback(
    () => (
      <Box className={classes.formContainer}>
        {labels.title && (
          <Typography variant="h4" className={classes.title}>
            {labels.title}
          </Typography>
        )}
        {labels.subtitle && (
          <Typography
            variant="body2"
            color="textPrimary"
            className={classes.subtitle}
          >
            {labels.subtitle}
          </Typography>
        )}
        <form className={classes.container} onSubmit={handleSubmit}>
          <Box container spacing={2}>
            <Box width="100%">
              <CodeInput
                onChange={handleCodeChange}
                fullWidth
                error={!!error}
                aria-describedby={error ? 'verification-error' : undefined}
                aria-label="Verification code input"
                disabled={processing || verificationLoading}
              />
            </Box>
            {error && (
              <Box textAlign="left" role="alert" aria-live="polite">
                <Typography className={classes.error} id="verification-error">
                  {error}
                </Typography>
              </Box>
            )}

            <ResendCodeLink
              isDisabled={isResendDisabled || verificationLoading || processing}
              isLocked={!!lockoutUntil}
              countdownSeconds={countdownSeconds}
              resendText={labels.resendText}
              resendTextWithTime={labels.resendTextWithTime}
              showSuccess={resent}
              successText={labels.resendSuccessText}
              onResend={handleResend}
            />
            <Box>
              <Button
                className={classes.button}
                type="submit"
                disabled={(processing && showLoadingOverlay) || !!lockoutUntil}
                aria-label="Submit verification code"
              >
                {(processing || verificationLoading || loading) &&
                !showLoadingOverlay ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  labels.submitText
                )}
              </Button>
            </Box>
            {labels.disclaimer && (
              <Box>
                <Typography className={classes.disclaimer}>
                  {labels.disclaimer}
                </Typography>
              </Box>
            )}
          </Box>
        </form>
      </Box>
    ),
    [
      classes,
      labels,
      handleSubmit,
      handleCodeChange,
      error,
      processing,
      isResendDisabled,
      lockoutUntil,
      handleResend,
      resent,
      isEmailChange,
      verificationLoading,
      countdownSeconds,
      showLoadingOverlay,
      loading,
    ],
  );

  const renderContent = useCallback(
    () => (
      <>
        {showLoadingOverlay && (
          <LoadingOverlay show={processing || verificationLoading} />
        )}
        {showSuccess ? renderSuccessContent() : renderVerificationForm()}
      </>
    ),
    [
      processing,
      verificationLoading,
      showSuccess,
      renderSuccessContent,
      renderVerificationForm,
    ],
  );

  if (!opened) return null;

  if (contentOnly) {
    return renderContent();
  }

  return (
    <ThemedDialog
      open={opened}
      onClose={handleClose}
      classes={{
        paper: classes.paper,
      }}
      showCloseIcon={showSuccess}
    >
      <DialogContent
        classes={{
          root: showSuccess
            ? classes.verificationSuccessContent
            : classes.contentRoot,
        }}
      >
        {renderContent()}
      </DialogContent>
    </ThemedDialog>
  );
};

VerificationCodeDialog.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  resendLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  successTitle: PropTypes.string,
  successButtonLabel: PropTypes.string,
  contentOnly: PropTypes.bool,
  type: PropTypes.oneOf([COMPONENT_TYPES.MODAL, COMPONENT_TYPES.PAGE]),
  onSuccess: PropTypes.func,
  showLoadingOverlay: PropTypes.bool,
  loading: PropTypes.bool,
  isEmbedded: PropTypes.bool,
};

VerificationCodeDialog.defaultProps = {
  successTitle: undefined,
  successButtonLabel: undefined,
  contentOnly: false,
  type: COMPONENT_TYPES.MODAL,
  showLoadingOverlay: true,
  loading: false,
  isEmbedded: false,
};

export default VerificationCodeDialog;
